import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    AdaptiveQuizAnswerRequest,
    AdaptiveQuizInfoResponse,
    AdaptiveQuizStatus,
    AdaptiveQuizUserQuestionsResponse,
    AnswerStatus,
    QuizNotification,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/adaptiveQuiz.api.ts';
import {
    getApiAdaptiveQuizData,
    postApiAdaptiveQuizAnswer,
    postApiAdaptiveQuizFinish,
    postApiAdaptiveQuizStart,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/adaptiveQuiz.api.ts';
import { getLearningOverview } from '@routes/sidebar/learning/___pages___/learning-overview/model/learningOverview.slice.ts';
import { COMMON_QUIZ_STATUS } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/quiz/___shared___/constants.ts';
import {
    applyAttemptsLeft,
    removeQuestionFromSkippedOnes,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/quiz/___shared___/helpers.ts';
import { extractError } from '@utils/slice.ts';

export interface LearningAdaptiveQuizSlice {
    adaptiveQuizData: AdaptiveQuizUserQuestionsResponse | null;
    adaptiveQuizIntroData: AdaptiveQuizInfoResponse | null;
    error: Record<string, any> | null;
    skippedQuestions: Record<number, number[]>;
    currentQuestionId: number | null;
    isAnyAnswerProvided: boolean;
    checkedAnswers: Record<number, number[]>;
    correctAnswers: Record<number, number[]>;
    answeredQuestions: number[];
    quizQuestionAnswerStatuses: Record<number, AnswerStatus>;
    quizFinishData: AdaptiveQuizInfoResponse | null;
    adaptiveQuizAnswers: AdaptiveQuizAnswerRequest[];
    toggleTimerInAdaptiveQuiz: boolean;
    showTimesUpModal: boolean;
    showUnansweredQuestionsModal: boolean;
    currentAdaptiveQuizStatus: AdaptiveQuizStatus | undefined;
    attemptsLeft: number;
    unAnsweredQuestionsAnfLifeTime: {
        remainingTimeSeconds: number;
        unansweredQuestionNumbers: number[];
    };
    quizNotification: QuizNotification | undefined;
    isAdaptiveQuizFinished: boolean;
    showSkipQuestionsNumbersBanner: boolean;

    isFetchAdaptiveQuizDataPending: boolean;
    isStartAdaptiveQuizPending: boolean;
    isPostAnswerAdaptiveQuizPending: boolean;
    isFinishAdaptiveQuizPending: boolean;
}

const initialState: LearningAdaptiveQuizSlice = {
    adaptiveQuizData: null,
    adaptiveQuizIntroData: null,
    error: null,
    skippedQuestions: {},
    currentQuestionId: null,
    isAnyAnswerProvided: false,
    checkedAnswers: {},
    correctAnswers: {},
    answeredQuestions: [],
    quizQuestionAnswerStatuses: {},
    quizFinishData: null,
    adaptiveQuizAnswers: [],
    toggleTimerInAdaptiveQuiz: false,
    showTimesUpModal: false,
    showUnansweredQuestionsModal: false,
    currentAdaptiveQuizStatus: undefined,
    attemptsLeft: 0,
    unAnsweredQuestionsAnfLifeTime: {
        remainingTimeSeconds: 0,
        unansweredQuestionNumbers: [],
    },
    quizNotification: undefined,
    isAdaptiveQuizFinished: false,
    showSkipQuestionsNumbersBanner: false,

    isFetchAdaptiveQuizDataPending: true,
    isStartAdaptiveQuizPending: false,
    isPostAnswerAdaptiveQuizPending: false,
    isFinishAdaptiveQuizPending: false,
};

export const fetchAdaptiveQuizData = createAsyncThunk<
    AdaptiveQuizInfoResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>('learningAdaptiveQuiz/fetchAdaptiveQuizData', ({ pathwayId, quizId }, { rejectWithValue }) =>
    getApiAdaptiveQuizData(pathwayId, quizId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const startAdaptiveQuiz = createAsyncThunk<
    AdaptiveQuizUserQuestionsResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>('learningAdaptiveQuiz/startAdaptiveQuiz', ({ pathwayId, quizId }, { rejectWithValue }) =>
    postApiAdaptiveQuizStart(pathwayId, quizId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const postAnswerAdaptiveQuiz = createAsyncThunk<
    any,
    {
        payload: AdaptiveQuizAnswerRequest;
        quizId: number;
        pathwayId: string;
    },
    { rejectValue: any }
>('learningAdaptiveQuiz/postApiAdaptiveQuizAnswer', ({ payload, quizId, pathwayId }, { rejectWithValue }) =>
    postApiAdaptiveQuizAnswer(payload, quizId, pathwayId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const finishAdaptiveQuizAndUpdatePathwayConditionally = createAsyncThunk<
    AdaptiveQuizInfoResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>(
    'learningAdaptiveQuiz/finishAdaptiveQuizAndUpdatePathwayConditionally',
    ({ pathwayId, quizId }, { dispatch, rejectWithValue }) => {
        dispatch(setIsAdaptiveQuizFinished(true));

        return postApiAdaptiveQuizFinish(pathwayId, quizId)
            .then((res) => {
                if (res.quizStatus === COMMON_QUIZ_STATUS.PASSED || res.quizStatus === COMMON_QUIZ_STATUS.FAILED) {
                    return dispatch(getLearningOverview(Number(pathwayId))).then(() => res);
                }

                return res;
            })
            .catch((err) => rejectWithValue(err));
    },
);

const learningAdaptiveQuizSlice = createSlice({
    name: 'learningAdaptiveQuiz',
    initialState,
    reducers: {
        setSkippedQuestions(state, action: PayloadAction<{ quizId: number; questionId: number }>) {
            const { quizId, questionId } = action.payload;
            const list = state.skippedQuestions[quizId] ?? (state.skippedQuestions[quizId] = []);
            if (!list.includes(questionId)) {
                list.push(questionId);
                state.showSkipQuestionsNumbersBanner = true;
            }
        },
        clearAllSkippedQuestions(state) {
            state.skippedQuestions = {};
        },
        setIsAnyAnswerProvided(state, action: PayloadAction<boolean>) {
            state.isAnyAnswerProvided = action.payload;
        },
        setCurrentQuestionId(state, action: PayloadAction<number | null>) {
            state.currentQuestionId = action.payload;
        },
        setCheckedAnswers(state, action: PayloadAction<{ questionId: number; answers: number[] }>) {
            const { questionId, answers } = action.payload;
            state.checkedAnswers[questionId] = answers;
        },
        setAdaptiveQuizAnswers(state, action: PayloadAction<{ questionId: number; answersIds: number[] }>) {
            const { questionId, answersIds } = action.payload;
            const existing = state.adaptiveQuizAnswers.find((answer) => answer.questionId === questionId);

            if (existing) {
                existing.answersIds = answersIds;
            } else {
                state.adaptiveQuizAnswers.push({ questionId, answersIds });
            }

            if (!state.answeredQuestions.includes(questionId)) {
                state.answeredQuestions.push(questionId);
            }
        },
        setToggleTimerInAdaptiveQuiz(state, action: PayloadAction<boolean>) {
            state.toggleTimerInAdaptiveQuiz = action.payload;
        },
        setShowTimesUpModal(state, action: PayloadAction<boolean>) {
            state.showTimesUpModal = action.payload;
        },
        setShowUnansweredQuestionsModal(state, action: PayloadAction<boolean>) {
            state.showUnansweredQuestionsModal = action.payload;
        },
        setUnAnsweredQuestionsAnfLifeTime(
            state,
            action: PayloadAction<Partial<LearningAdaptiveQuizSlice['unAnsweredQuestionsAnfLifeTime']>>,
        ) {
            state.unAnsweredQuestionsAnfLifeTime = { ...state.unAnsweredQuestionsAnfLifeTime, ...action.payload };
        },
        clearAdaptiveQuizData(state) {
            state.adaptiveQuizAnswers = [];
            state.currentQuestionId = null;
            state.answeredQuestions = [];
            state.checkedAnswers = {};
            state.skippedQuestions = {};
            state.unAnsweredQuestionsAnfLifeTime = {
                remainingTimeSeconds: 0,
                unansweredQuestionNumbers: [],
            };
        },
        setIsAdaptiveQuizFinished(state, action: PayloadAction<boolean>) {
            state.isAdaptiveQuizFinished = action.payload;
        },
        showSkipQuestionsNumbersBanner(state, action: PayloadAction<boolean>) {
            state.showSkipQuestionsNumbersBanner = action.payload;
        },
        // resets necessary data so any existing transition screens (e.g. outro) can be cleared before new quiz starts
        resetAdaptiveQuizFlow(state) {
            state.isAdaptiveQuizFinished = false;
            state.currentAdaptiveQuizStatus = undefined;
            state.quizFinishData = null;
        },
        clearAdaptiveQuizFinishData(state) {
            state.quizFinishData = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(startAdaptiveQuiz.pending, (state) => {
            state.error = null;
            state.isStartAdaptiveQuizPending = true;
            state.currentAdaptiveQuizStatus = undefined;
        });
        builder.addCase(startAdaptiveQuiz.fulfilled, (state, action) => {
            state.adaptiveQuizData = action.payload;
            state.currentQuestionId = action.payload.adaptiveQuizQuestion[0]?.id ?? null;
            state.currentAdaptiveQuizStatus = action.payload.adaptiveQuizDescription.quizStatus;
            state.toggleTimerInAdaptiveQuiz = true;
            state.showTimesUpModal = false;
            state.isStartAdaptiveQuizPending = false;
        });
        builder.addCase(startAdaptiveQuiz.rejected, (state, action) => {
            state.error = extractError(action);
            state.isStartAdaptiveQuizPending = false;
        });

        builder.addCase(fetchAdaptiveQuizData.pending, (state) => {
            state.error = null;
            state.isFetchAdaptiveQuizDataPending = true;
            state.currentAdaptiveQuizStatus = undefined;
            state.attemptsLeft = 0;
            state.quizNotification = undefined;
        });
        builder.addCase(fetchAdaptiveQuizData.fulfilled, (state, action) => {
            state.adaptiveQuizIntroData = action.payload;
            state.quizFinishData = action.payload;
            state.error = null;
            state.currentAdaptiveQuizStatus = action.payload.quizStatus;
            applyAttemptsLeft(state, action.payload);
            state.quizNotification = action.payload.quizNotification;
            state.isAdaptiveQuizFinished = false;
            state.isFetchAdaptiveQuizDataPending = false;
        });
        builder.addCase(fetchAdaptiveQuizData.rejected, (state, action) => {
            state.error = extractError(action);
            state.isFetchAdaptiveQuizDataPending = false;
        });

        builder.addCase(postAnswerAdaptiveQuiz.pending, (state) => {
            state.error = null;
            state.isPostAnswerAdaptiveQuizPending = true;
        });
        builder.addCase(postAnswerAdaptiveQuiz.fulfilled, (state, action) => {
            state.showSkipQuestionsNumbersBanner = false;
            const questionId = action.meta.arg.payload.questionId;
            const quizId = action.meta.arg.quizId;
            removeQuestionFromSkippedOnes(state, questionId, quizId);
            state.isPostAnswerAdaptiveQuizPending = false;
        });
        builder.addCase(postAnswerAdaptiveQuiz.rejected, (state, action) => {
            state.error = extractError(action);
            state.isPostAnswerAdaptiveQuizPending = false;
        });

        builder.addCase(finishAdaptiveQuizAndUpdatePathwayConditionally.pending, (state) => {
            state.error = null;
            state.isFinishAdaptiveQuizPending = true;
        });
        builder.addCase(finishAdaptiveQuizAndUpdatePathwayConditionally.fulfilled, (state, action) => {
            state.quizFinishData = action.payload;
            state.currentAdaptiveQuizStatus = action.payload.quizStatus;
            applyAttemptsLeft(state, action.payload);
            state.quizNotification = action.payload.quizNotification;
            state.isFinishAdaptiveQuizPending = false;
        });
        builder.addCase(finishAdaptiveQuizAndUpdatePathwayConditionally.rejected, (state, action) => {
            state.error = extractError(action);
            state.isFinishAdaptiveQuizPending = false;
        });
    },
});

export const {
    setSkippedQuestions,
    setIsAnyAnswerProvided,
    setCurrentQuestionId,
    setCheckedAnswers,
    clearAllSkippedQuestions,
    setAdaptiveQuizAnswers,
    setToggleTimerInAdaptiveQuiz,
    setShowTimesUpModal,
    setShowUnansweredQuestionsModal,
    setUnAnsweredQuestionsAnfLifeTime,
    clearAdaptiveQuizData,
    setIsAdaptiveQuizFinished,
    showSkipQuestionsNumbersBanner,
    resetAdaptiveQuizFlow,
    clearAdaptiveQuizFinishData,
} = learningAdaptiveQuizSlice.actions;
export default learningAdaptiveQuizSlice.reducer;
